import React from 'react';
import {
  GuidePage, Section, Steps, QuestionList, WhatHappensNext, Trouble, Footnote, Para, Bullets, Callout,
  startCaseStep, subjectRow, troubleSleeveRows, TROUBLE_CATEGORY_MISSING, TROUBLE_WRONG_HOUSEHOLD,
} from '../GuideKit';

/* Operations → Roth Conversion(s) → Full Roth Conversion / Partial Roth Conversion.
 *
 * Written against the live flow Prosperity_Workflow_Create_New_Case_Ops
 * (version active 2026-10-04): screens "Roth Conversion(s) Screen", "New
 * Account Case Required", and "FULL Roth Conversion(s) Screen" / "Partial Roth
 * Conversion(s) Screen". Full and Partial are the same screen; only the
 * workflow template stamped on the case differs. Shared chrome and policy
 * copy live in GuideKit.js.
 */

const STEPS = [
  startCaseStep({
    recordType: 'Operations',
    category: 'Roth Conversion(s)',
    subCategory: 'Full Roth Conversion or Partial Roth Conversion',
    subject: subjectRow('Roth conversion - 0989-022356'),
    lookups: [['Household', 'The client household. It decides which accounts you can pick later, and the advisor and office are taken from it.']],
    note: (
      <>
        <strong>Full and Partial ask exactly the same questions.</strong> The choice is recorded on the case so
        Operations knows the intent - for a partial, say how much or which holdings in the details box.
      </>
    ),
  }),
  {
    title: 'Answer the Roth Conversion Details questions',
    body: (
      <p style={{ margin: 0 }}>
        Questions reveal themselves one at a time based on your answers, so you only see what applies. Every
        question in the reference below is required once it appears.
      </p>
    ),
  },
  {
    title: 'Describe the conversion and choose the two accounts',
    body: (
      <>
        <p style={{ marginBottom: '0.5rem' }}>
          The next screen opens with a single box: <em>Please provide as many details as possible for the
          Operations Team...</em> Fill it in first - nothing else on the screen appears until you do. It becomes
          the case description Operations reads first, so include partial amounts, specific holdings to convert
          in kind, and any client timing requests.
        </p>
        <p style={{ margin: 0 }}>
          Two tables then appear: <strong>IRA Accounts</strong> (the source) and <strong>Roth IRA Accounts</strong>{' '}
          (the destination). Pick one row in each - both are required. Each shows Account Details, Model, Cash
          and Balance.
        </p>
      </>
    ),
  },
  {
    title: 'Acknowledge and submit',
    body: (
      <p style={{ margin: 0 }}>
        Read the Operations and Trading notice under the tables - it governs timing and what the teams can and
        cannot do. Select <em>I have read, understand, and agree to the above information</em> under
        Acknowledgement and click Next. FSC creates the case and opens it. Note the case number for follow-up.
      </p>
    ),
  },
];

// Every question on the Roth Conversion(s) Screen, in the order it can appear.
const QUESTIONS = [
  {
    q: 'Is this for a new or existing Roth IRA?',
    when: 'Always - this is the first question',
    options: ['New (Roth IRA)', 'Existing (Roth IRA)'],
  },
  {
    q: 'Has the Roth Account Application already been submitted at the Custodian?',
    when: 'Only if you chose New (Roth IRA)',
    options: ['Yes (It has already been submitted)', 'No (It has not been submitted)'],
    note: 'Answering No ends the questions on this screen, and Next takes you to a New Account Case Required message - see the warning below the list.',
    warn: true,
  },
  {
    q: 'Is the Account open? (Do you know the Account Number yet?)',
    when: 'Only if the new application has already been submitted',
    options: ['Yes (The Account is open)', 'No (The Account is not open)'],
    note: 'Answering No has the same result as above - the conversion cannot be requested until the account is open.',
    warn: true,
  },
  {
    q: 'Which Custodian?',
    when: 'If the Roth is existing, or the new account is open',
    options: ['Schwab', 'Fidelity'],
  },
  {
    q: 'Are the funds being converted into the same Model the IRA is currently in, the same Model the Roth IRA is currently in, or neither?',
    when: 'Once a custodian is selected',
    options: ['Same Model as IRA', 'Same Model as Roth IRA', 'New Model', 'None of the Above'],
  },
  {
    q: 'If "New Model" or "None of the Above", Please Explain?',
    when: 'Only if you chose New Model or None of the Above',
    note: 'Name the target model or describe the intended allocation. Vague answers here are the most common cause of a follow-up call.',
  },
  {
    q: 'Will Trades need to be made for this Conversion?',
    when: 'Once the model question is answered (and explained, if needed)',
    options: ['Yes (Trades will need to be made)', 'No (Trades are not yet needed)'],
  },
  {
    q: 'Will the Roth IRA be charged the same Fee Schedule as before (or as the IRA)?',
    when: 'After the trades question',
    options: ['Yes (Same Fee Schedule: NOT CHANGING)', 'No (Different Fee Schedule: LOWER)', 'No (Different Fee Schedule: HIGHER)'],
    note: 'Answering Yes skips the two fee questions below and goes straight to the conversion value.',
  },
  {
    q: 'Please provide the new Fee Schedule details',
    when: 'Only if the fee schedule is changing, lower or higher',
  },
  {
    q: 'Do you have a New/Updated and Signed Schedule A? PLEASE NOTE, WE CAN NOT CHARGE THE NEW FEE SCHEDULE WITHOUT THIS. FAILURE TO SUBMIT WILL RESULT IN A NIGO!!!',
    when: 'Once you have entered the new fee schedule details',
    options: ['Yes (I have an updated Schedule A)', 'No (I do not have an updated Schedule A)'],
    note: 'The form lets you continue either way, and it has no upload for the document. Prosperity cannot charge a new fee schedule without a signed Schedule A, so a request without one is returned NIGO - have it signed before you submit.',
    warn: true,
  },
  {
    q: 'What is the estimated value of the Conversion?',
    when: 'Once the Schedule A question is answered, or straight away if the fee schedule is not changing',
    note: 'A reasonable estimate is fine - it sets expectations, it is not an execution instruction.',
  },
  {
    q: 'Do you wish to withhold taxes?',
    when: 'Only if the estimated value is more than $100',
    options: ['Yes (Withhold Taxes)', 'No (Do Not Withhold Taxes)'],
  },
  {
    q: 'Federal Tax Withheld · State Tax Withheld',
    when: 'Only if you chose to withhold taxes',
    note: 'Enter each as a percentage - 10 means 10%, not $10. The form then shows the Net Distribution after both are taken off; check it against what you told the client.',
  },
];

const TROUBLE = [
  TROUBLE_CATEGORY_MISSING('Operations', 'Roth Conversion(s)'),
  {
    h: 'I got a "New Account Case Required" message',
    p: [
      'You said the new Roth has not been submitted to the custodian, or is not open yet. Operations cannot convert into an account that does not exist. Cancel this request, open a separate case for it under New Business → New Account (Existing Client), and submit the conversion once it is open.',
      'The form will let you click past the message, but the new Roth will not be in the Roth IRA Accounts table, so do not try to finish the request.',
    ],
  },
  {
    h: 'The Roth IRA Accounts table is empty',
    p: [
      'It only lists Roth registrations - Roth IRA, Contributory Roth, Conversion Roth, Roth 401(K), Beneficiary Roth IRA, Roth Educational Trust, Minor Roth or Roth Rollover IRA. If the client’s Roth is missing, its registration type is most likely set to something else.',
      'The IRA Accounts table likewise only lists SEP IRA, SIMPLE, IRA Rollover, Contributory IRA, Rollover IRA, Traditional IRA and Individual Retirement Account registrations, and only those with a balance above zero.',
    ],
  },
  troubleSleeveRows('Inactive sleeves, and cancelled sleeves ending in CNX, are filtered out of both tables.'),
  {
    h: "The tables and notice haven't appeared",
    p: ['They stay hidden until the details box at the top of the screen has something in it.'],
  },
  TROUBLE_WRONG_HOUSEHOLD,
];

export default function RothConversions() {
  return (
    <GuidePage
      title="Roth Conversion Requests"
      icon="arrow-left-right"
      path="Operations → Roth Conversion(s) → Full or Partial Roth Conversion"
      minutes={5}
      intro="How to submit a Roth conversion to the Prosperity Operations team - what to gather first, every question you will be asked, and what happens after you submit."
    >
      <Section title="Before you start" icon="clipboard-check">
        <Para>Have these ready. The form branches on your answers, and some paths cannot be completed without them.</Para>
        <Bullets items={[
          <><strong>The IRA the money is coming from</strong> and <strong>the Roth it is going into</strong>, both under the same household and both open. You pick them from a list, so you do not need account numbers memorised.</>,
          <><strong>The custodian</strong> - Schwab or Fidelity.</>,
          <><strong>The estimated conversion value</strong>, and for a partial conversion the amount or holdings to convert.</>,
          <><strong>A signed Schedule A</strong>, if the Roth will be charged a different fee schedule. Without it the request is returned NIGO.</>,
          <><strong>The tax withholding decision</strong>, with federal and state percentages if the client wants tax withheld.</>,
        ]} />
        <Callout>
          If the Roth does not exist yet, open it first with a New Business → New Account (Existing Client) case. The conversion request needs the
          Roth to be open at the custodian.
        </Callout>
      </Section>

      <Section title="Submitting the request" icon="list-ol">
        <Steps steps={STEPS} />
      </Section>

      <Section title="Every question, and when it appears" icon="question-circle">
        <Para>Use this to prepare before you sit down with a client, or to work out why a question you expected has not shown up.</Para>
        <QuestionList questions={QUESTIONS} />
      </Section>

      <WhatHappensNext team="operations" />

      <Trouble items={TROUBLE} />

      <Footnote>
        This guide describes the request form only. It is not tax advice, and it does not describe the
        suitability review or the conversion&rsquo;s tax consequences for the client.
      </Footnote>
    </GuidePage>
  );
}
